import React, { useState } from 'react'
import { Box, TextField, Typography, useTheme } from '@mui/material'
import { FormDivider } from '../Elements/FormDivider'
import Header, { HeaderVariant } from '../Elements/Header'
import CardFormHeader from '../Card/CardFormHeader'
import { useTranslation } from 'react-i18next'
import { useShowOnDesktop } from '../../hooks/useShowOnDesktop'
import { useTranscriptContext } from '../Context/TranscriptContext'
import BasicModal from '../Modals/BasicModal'
import ActionButtons from '../Buttons/ActionButtons'
import { ContainedButtonVariant } from '../Buttons/ContainedButton'

export interface SchoolInformationHeaderProps {
  isFieldDisabled?: boolean
}

export const SchoolInformationHeader: React.FC<
  SchoolInformationHeaderProps
> = ({ isFieldDisabled }) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const showOnDesktop = useShowOnDesktop()
  const {
    updateTranscriptDetails,
    transcriptDetails,
    transcriptValidity,
    updateFormValidity,
  } = useTranscriptContext()

  const [isSchoolWarningOpen, setIsSchoolWarningOpen] = useState(false)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value.match(/classical conversations/i) !== null) {
      setIsSchoolWarningOpen(true)

      updateFormValidity({
        ...transcriptValidity,
        [event.target.name]: { input: true },
      })
      updateTranscriptDetails({
        ...transcriptDetails,
        [event.target.name]: event.target.value,
      })
    } else {
      updateFormValidity({
        ...transcriptValidity,
        [event.target.name]: { input: true },
      })
      updateTranscriptDetails({
        ...transcriptDetails,
        [event.target.name]: event.target.value,
      })
    }
  }

  return (
    <section aria-labelledby="school-information-header">
      <BasicModal
        isOpen={isSchoolWarningOpen}
        dialogActions={
          <ActionButtons primaryButtonLabel={ContainedButtonVariant.Ok} />
        }
        dialogContent={
          <Box maxWidth={300} mx="auto" textAlign="center">
            <Typography component="p">
              {t(
                'Transcript.SchoolInformationHeader.Modal.SchoolWarning.Content',
                `Classical Conversations, Inc. is a registered business name. Please do not use it to name your homeschool
with your state department of education or Secretary of State. We suggest a name similar to Smith’s Classical
Academy. Please feel free to use Classical Conversations as your curriculum source. DO NOT enroll your home
school in partnership with Classical Conversations on your state’s homeschool registration form. Classical
Conversations is not a school.`
              )}
            </Typography>
          </Box>
        }
        handleFormSubmit={() => setIsSchoolWarningOpen(false)}
        maxWidth={'xs'}
      />
      <CardFormHeader
        headerContainerProps={{
          paddingTop: 2,
          [theme.breakpoints.down('sm')]: {
            marginLeft: 0,
          },
        }}
        header={
          <Header
            id="school-information-header"
            component={'h2'}
            variant={HeaderVariant.Card}
            headerName={t(
              'Transcripts.SchoolInformationHeader.Header.SchoolInformation',
              'School Information'
            )}
          />
        }
      />
      <Box
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 2 }}
        ml={2}
      >
        <TextField
          value={transcriptDetails.schoolName}
          helperText={
            !transcriptValidity.schoolName.input
              ? t(
                  'Transcripts.SchoolInformationHeader.Error.SchoolName',
                  'School Name is Required.'
                )
              : undefined
          }
          error={!transcriptValidity.schoolName.input}
          id="school name"
          name="schoolName"
          label={t(
            'Transcripts.SchoolInformationHeader.Field.SchoolName',
            'School Name'
          )}
          variant="filled"
          disabled={isFieldDisabled}
          sx={{
            mx: showOnDesktop ? 2 : 0,
            marginRight: 2,
            maxWidth: '48%',
          }}
          fullWidth
          onChange={handleChange}
          required
        />
      </Box>
      <FormDivider sx={{ marginX: theme.spacing(2) }} />
    </section>
  )
}

export default SchoolInformationHeader
